import { createContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Pin, X } from 'lucide-react';
import styles from './BottomSheet.module.css';

/**
 * Surface context. A BottomSheet opened with `glass` publishes `true` here so
 * the rows inside it (ListRow consumes this) render as glass tiles rather than
 * flat rows — the glassmorphism UI language, applied to the whole sheet with a
 * single prop instead of threading it through every row. Default false, so
 * non-glass sheets and standalone ListRows are unchanged.
 */
export const SheetSurfaceContext = createContext(false);

export interface BottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  /** Backdrop styling. 'default' (the default) is the plain dark dim; 'brand'
   *  layers a subtle EV Initiative green glow over that same dim, so a sheet
   *  can feel branded as it opens (EVIE uses this). Other sheets omit it and
   *  keep the plain backdrop. */
  backdropTone?: 'default' | 'brand';
  /** Optional leading element rendered left of the title in the header
   *  (EVIE passes her orb avatar). Purely decorative — the h2 keeps the
   *  aria-labelledby contract on its own. */
  titleIcon?: ReactNode;
  /** Optional control rendered in the header's right cluster, before the
   *  close button (EVIE passes her history three-dot). The caller owns
   *  its behavior and accessibility. */
  headerAction?: ReactNode;
  /** Desktop-only width. 'default' keeps the standard centered-modal max
   *  width; 'wide' widens the sheet at the >=1024px breakpoint only (the
   *  Profile menu sheets use this so they get desktop dignity to match the
   *  widened Profile page). Below 1024px the sheet is full-width either
   *  way, so this has no effect on mobile. */
  size?: 'default' | 'wide';
  /** Glass material: the sheet panel becomes translucent instrument-panel
   *  glass and its rows render as glass tiles (via SheetSurfaceContext). The
   *  Profile submenu sheets use it. Default false keeps the solid surface. */
  glass?: boolean;
  /** Opt out of the standard 16px horizontal body inset and render the
   *  content edge-to-edge. The body is padded by default (the spacing law);
   *  set this only on sheets that genuinely manage their own gutter (e.g. a
   *  full-bleed media or a content wrapper that already insets). */
  fullBleed?: boolean;
  /** Pinned (sticky) sheet: renders a subtle token-styled cue — the drag
   *  handle gains an accent treatment and a small pin badge sits by the title —
   *  to signal that this open is sticky (survives navigating away and back).
   *  Dismiss controls (X / scrim / swipe) still call onClose. Default false. */
  pinned?: boolean;
}

/**
 * Mobile-first bottom sheet. Renders nothing when `isOpen` is
 * false; on transition to true, fades in the backdrop + slides
 * the card up from the bottom edge. The reduced-motion media
 * query in the CSS swaps the slide for an instant appear.
 *
 * Dismissal paths:
 *   - Backdrop tap (mousedown on the overlay outside the card)
 *   - Close X in the header
 *   - ESC key
 *   - Swipe-down gesture on the card (touch handlers below)
 *
 * Accessibility:
 *   - role="dialog" + aria-modal="true" + aria-labelledby on
 *     the header h2
 *   - Initial focus moves to the close button so keyboard users
 *     can dismiss without hunting for it
 *   - Focus trap inside the sheet while open (Tab / Shift+Tab
 *     loop within focusable descendants)
 *   - body scroll locked while open
 *
 * Desktop fallback (≥1024px viewport): the CSS re-anchors the
 * overlay to center alignment and rounds all four corners so the
 * sheet looks like a centered modal — relevant for the rare
 * desktop PWA install.
 *
 * Why portal: the menu page wrappers in `AppShell` apply
 * `animation: evi-page-enter ... both` whose final keyframe sets
 * `transform: translateY(0)` on the wrapper. A non-`none`
 * transform on an ancestor promotes that ancestor to a CSS
 * containing block for `position:fixed` descendants — at which
 * point the sheet's `bottom: 0` anchors to the wrapper, NOT the
 * viewport. AppShell already documents this trap and works
 * around it on fullscreen routes by using an opacity-only
 * animation; the menu routes don't have that out. Portaling the
 * sheet straight to `document.body` puts it OUTSIDE the
 * transformed wrapper so its `position:fixed` always anchors to
 * the viewport regardless of what page-transition animations
 * the rest of the app introduces in the future.
 */
export function BottomSheet({
  isOpen, onClose, title, children, backdropTone = 'default', titleIcon, headerAction,
  size = 'default', glass = false, fullBleed = false, pinned = false,
}: BottomSheetProps) {
  const sheetRef = useRef<HTMLDivElement | null>(null);
  const closeBtnRef = useRef<HTMLButtonElement | null>(null);
  // Drag-Y in pixels for the swipe-down gesture. We track this in
  // state so the inline transform updates as the user drags; the
  // pointer-up handler decides whether to commit the dismiss
  // (drag past threshold) or snap back to 0 (under threshold).
  const [dragY, setDragY] = useState(0);
  const dragStartY = useRef<number | null>(null);
  // Latched at touch start: is this gesture a card-drag (dismiss-eligible)
  // or a content-scroll? Decided once so the mode never flips mid-swipe.
  const dragEligible = useRef<boolean>(true);
  // Body-scroll restore. We remember the document overflow before
  // locking so we don't clobber a non-default value set elsewhere.
  const previousBodyOverflow = useRef<string>('');
  // Ghost-dismiss guard. On Android, a quick tap dispatches compat mouse
  // events AFTER touchend at the tap point — if that tap is what OPENED this
  // sheet, the overlay now covers the point and the ghost mousedown would
  // dismiss the sheet in the same breath it opened (tap on a Profile card
  // looked like "the sheet never opens"; a long-press survives only because
  // Android suppresses compat events after long-press gestures). Stamp the
  // open moment and ignore overlay dismissals inside the ghost window.
  const openedAt = useRef(0);
  useEffect(() => {
    if (isOpen) openedAt.current = Date.now();
  }, [isOpen]);

  // ── Body scroll lock + initial focus + ESC dismiss ──
  useEffect(() => {
    if (!isOpen) return;
    previousBodyOverflow.current = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Move focus into the sheet so the focus trap has somewhere
    // to anchor and keyboard users can ESC to dismiss immediately.
    closeBtnRef.current?.focus();

    const handleKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      // Focus trap. Tab / Shift+Tab cycles through focusable
      // descendants of the sheet; without this, Tab would
      // escape into the page underneath.
      if (e.key === 'Tab' && sheetRef.current) {
        const focusables = sheetRef.current.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        );
        if (focusables.length === 0) return;
        const first = focusables[0]!;
        const last = focusables[focusables.length - 1]!;
        const active = document.activeElement;
        if (e.shiftKey && active === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => {
      window.removeEventListener('keydown', handleKey);
      document.body.style.overflow = previousBodyOverflow.current;
    };
  }, [isOpen, onClose]);

  // Reset drag state whenever the sheet re-opens — defensive in
  // case a previous drag landed near the dismiss threshold and
  // the sheet stays stuck visually displaced on next open.
  useEffect(() => {
    if (isOpen) setDragY(0);
  }, [isOpen]);

  if (!isOpen) return null;

  // ── Swipe-down gesture handlers ──
  // The card listens to touchstart/touchmove/touchend. A gesture is
  // dismiss-eligible only when it isn't a content scroll: that is decided
  // once at touch start from the nearest scrollable ancestor (see
  // handleTouchStart). When eligible, we commit displacement for downward
  // drags and dismiss once the drag passes a flat DISMISS_THRESHOLD_PX.
  // Upward drags are clamped to 0 so the user can't pull the card
  // off-screen upward.
  const DISMISS_THRESHOLD_PX = 80;

  // Decide ONCE per gesture whether this is a card-drag (dismiss-eligible)
  // or a content-scroll. Walk up from the touched element to the sheet root
  // looking for the nearest scrollable ancestor (overflow-y auto/scroll with
  // actual overflow). The gesture is eligible to dismiss only when there is
  // no such ancestor (the touch landed on the handle or header) or it is
  // already scrolled to the top; when the content can still scroll up, the
  // gesture belongs to the scroller and must never dismiss the sheet.
  const handleTouchStart = (e: React.TouchEvent): void => {
    dragStartY.current = e.touches[0]?.clientY ?? null;
    let scroller: HTMLElement | null = null;
    let node: HTMLElement | null = e.target as HTMLElement;
    const root = sheetRef.current;
    while (node) {
      const overflowY = window.getComputedStyle(node).overflowY;
      if ((overflowY === 'auto' || overflowY === 'scroll')
        && node.scrollHeight > node.clientHeight) {
        scroller = node;
        break;
      }
      if (node === root) break;
      node = node.parentElement;
    }
    dragEligible.current = !scroller || scroller.scrollTop <= 0;
  };

  const handleTouchMove = (e: React.TouchEvent): void => {
    // Not eligible -> let native scroll consume the gesture; never drag.
    if (dragStartY.current == null || !dragEligible.current) return;
    const currentY = e.touches[0]?.clientY ?? dragStartY.current;
    const delta = currentY - dragStartY.current;
    setDragY(delta > 0 ? delta : 0);
  };

  const handleTouchEnd = (): void => {
    // Dismiss only when the gesture was an eligible drag past the threshold;
    // a scroll gesture left dragY at 0, so this just clears the state.
    if (dragEligible.current && dragY > DISMISS_THRESHOLD_PX) {
      onClose();
    } else {
      setDragY(0);
    }
    dragStartY.current = null;
  };

  // Click on the overlay (not the card itself) dismisses. We
  // gate on event.target === currentTarget so a click bubbling
  // up from a button inside the card doesn't dismiss the sheet,
  // and on the ghost window (see openedAt above) so the opening
  // tap's compat mousedown can't instantly close it.
  const handleOverlayClick = (e: React.MouseEvent): void => {
    if (Date.now() - openedAt.current < 400) return;
    if (e.target === e.currentTarget) onClose();
  };

  // Portal target = document.body so the sheet's position:fixed
  // is unambiguously anchored to the viewport, never to a
  // transformed page-transition wrapper. Defensive `typeof` guard
  // for SSR / pre-hydration contexts where `document` doesn't
  // exist (Vite is client-rendered today, but the guard keeps the
  // component portable).
  if (typeof document === 'undefined') return null;

  const sheetTree = (
    <div
      className={backdropTone === 'brand' ? `${styles.overlay} ${styles.overlayBrand}` : styles.overlay}
      role="presentation"
      onMouseDown={handleOverlayClick}
    >
      <div
        ref={sheetRef}
        className={[
          styles.sheet,
          size === 'wide' ? styles.sheetWide : '',
          glass ? styles.sheetGlass : '',
        ].filter(Boolean).join(' ')}
        role="dialog"
        aria-modal="true"
        aria-labelledby="evi-sheet-title"
        // Inline transform applies the drag offset on top of the
        // CSS slide-in keyframe. transitionProperty:none while the
        // user is actively dragging keeps the card glued to the
        // finger; on release we let CSS take over for the snap-back.
        style={dragY > 0
          ? { transform: `translateY(${dragY}px)`, transition: 'none' }
          : {}}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div
          className={[styles.handle, pinned ? styles.handlePinned : ''].filter(Boolean).join(' ')}
          aria-hidden
        />
        <div className={styles.header}>
          <div className={styles.titleWrap}>
            {titleIcon}
            <h2 id="evi-sheet-title" className={styles.title}>{title}</h2>
            {pinned && <Pin size={13} className={styles.pinBadge} aria-label="Pinned" />}
          </div>
          <div className={styles.headerBtns}>
            {headerAction}
            <button
              ref={closeBtnRef}
              type="button"
              className={styles.closeBtn}
              onClick={onClose}
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </div>
        </div>
        <div className={[styles.body, fullBleed ? styles.bodyFullBleed : ''].filter(Boolean).join(' ')}>
          <SheetSurfaceContext.Provider value={glass}>{children}</SheetSurfaceContext.Provider>
        </div>
      </div>
    </div>
  );

  return createPortal(sheetTree, document.body);
}
